import React from "react";
import { Box, SxProps, Theme } from "@mui/material";

interface SecurityIconProps {
  sx?: SxProps<Theme>;
  /** Stroke colour; defaults to the surrounding text colour. */
  color?: string;
  strokeWidth?: number;
}

/**
 * Inline line icons traced from the Security design. Sizes and colours are
 * supplied by the caller from `Security.styles.ts` / `Security.tokens.ts` so no
 * design value is baked in here.
 */
const StrokeIcon: React.FC<
  SecurityIconProps & { children: React.ReactNode }
> = ({ sx, color, strokeWidth = 2, children }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color ?? "currentColor"}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    sx={sx}
  >
    {children}
  </Box>
);

/** Handset inside the Multi-Factor Authentication tile. */
export const MfaPhoneIcon: React.FC<SecurityIconProps> = (props) => (
  <StrokeIcon {...props}>
    <rect x="6" y="2" width="12" height="20" rx="2" />
    <path d="M12 18h.01" />
  </StrokeIcon>
);

/** Padlock in the "No passkeys yet" circle. */
export const PasskeyLockIcon: React.FC<SecurityIconProps> = (props) => (
  <StrokeIcon {...props}>
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    <rect x="5" y="11" width="14" height="9" rx="2" />
  </StrokeIcon>
);

/** Desktop monitor leading a session row. */
export const DeviceDesktopIcon: React.FC<SecurityIconProps> = (props) => (
  <StrokeIcon {...props}>
    <rect x="2" y="4" width="20" height="13" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
  </StrokeIcon>
);

/** Phone leading a session row opened on a handset. */
export const DeviceMobileIcon: React.FC<SecurityIconProps> = (props) => (
  <StrokeIcon {...props}>
    <rect x="6" y="2" width="12" height="20" rx="2" />
    <path d="M12 18h.01" />
  </StrokeIcon>
);

/** Tablet leading a session row opened on a tablet. */
export const DeviceTabletIcon: React.FC<SecurityIconProps> = (props) => (
  <StrokeIcon {...props}>
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M12 18h.01" />
  </StrokeIcon>
);

/** Key glyph leading a saved passkey row. */
export const PasskeyRowIcon: React.FC<SecurityIconProps> = (props) => (
  <StrokeIcon {...props}>
    <circle cx="7.5" cy="15.5" r="4.5" />
    <path d="M10.7 12.3L21 2" />
    <path d="M17 6l3 3" />
  </StrokeIcon>
);

/** Pencil in a passkey row's rename action. */
export const RowEditIcon: React.FC<SecurityIconProps> = (props) => (
  <StrokeIcon {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </StrokeIcon>
);

/** Bin in a passkey row's delete action. */
export const RowDeleteIcon: React.FC<SecurityIconProps> = (props) => (
  <StrokeIcon {...props}>
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </StrokeIcon>
);
